import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, Crop, FileImage, Loader2, AlertCircle, CheckCircle, Settings, RotateCcw } from 'lucide-react';
import { API_CONFIG, getApiUrl, validateFileSize, validateFileType, formatFileSize } from '../config/api';

interface CropSettings {
  x: number;
  y: number;
  width: number;
  height: number;
  aspectRatio: number | null;
  outputFormat: string;
  quality: number;
}

interface CropPreset {
  name: string;
  description: string;
  aspect_ratio: number | null;
}

interface CropResult {
  file: File;
  originalSize: number;
  croppedSize: number;
  originalDimensions: string;
  croppedDimensions: string;
  downloadUrl: string;
}

const ImageCropper: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{width: number, height: number} | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<CropResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [presets, setPresets] = useState<Record<string, CropPreset>>({});
  const [selectedPreset, setSelectedPreset] = useState<string>('free');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{x: number, y: number} | null>(null);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [displayScale, setDisplayScale] = useState(1);
  
  const [settings, setSettings] = useState<CropSettings>({
    x: 0,
    y: 0,
    width: 200,
    height: 200,
    aspectRatio: null,
    outputFormat: 'original',
    quality: 95,
  });

  // Load presets on mount
  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const response = await fetch(getApiUrl('/imagetool/presets/crop'));
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets);
      }
    } catch (error) {
      console.error('Failed to load presets:', error);
      // Set default presets if API fails
      setPresets({
        free: { name: 'Free Form', description: 'No constraint', aspect_ratio: null },
        square_1_1: { name: 'Square (1:1)', description: 'Perfect square', aspect_ratio: 1 },
        landscape_16_9: { name: 'Landscape (16:9)', description: 'Widescreen', aspect_ratio: 16/9 },
        portrait_9_16: { name: 'Portrait (9:16)', description: 'Vertical', aspect_ratio: 9/16 },
      });
    }
  };

  const applyPreset = (presetKey: string) => {
    const preset = presets[presetKey];
    if (preset && imageDimensions) {
      setSelectedPreset(presetKey);
      const newAspectRatio = preset.aspect_ratio;
      setSettings(prev => {
        let newWidth = prev.width;
        let newHeight = prev.height;
        
        if (newAspectRatio !== null) {
          // Adjust dimensions to match aspect ratio
          if (newAspectRatio >= 1) {
            newHeight = Math.round(newWidth / newAspectRatio);
          } else {
            newWidth = Math.round(newHeight * newAspectRatio);
          }
          
          // Ensure within bounds
          if (prev.x + newWidth > imageDimensions.width) {
            newWidth = imageDimensions.width - prev.x;
            newHeight = Math.round(newWidth / newAspectRatio);
          }
          if (prev.y + newHeight > imageDimensions.height) {
            newHeight = imageDimensions.height - prev.y;
            newWidth = Math.round(newHeight * newAspectRatio);
          }
        }
        
        return { ...prev, aspectRatio: newAspectRatio, width: newWidth, height: newHeight };
      });
    }
  };

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const selectedFile = selectedFiles[0];
    
    if (!validateFileType(selectedFile)) {
      setError('Unsupported file type');
      return;
    }
    if (!validateFileSize(selectedFile)) {
      setError(`File too large (max ${formatFileSize(API_CONFIG.MAX_FILE_SIZE)})`);
      return;
    }

    setError(null);
    setFile(selectedFile);
    setResult(null);
    
    // Create image URL and get dimensions
    const url = URL.createObjectURL(selectedFile);
    setImageUrl(url);
    
    const img = new window.Image();
    img.onload = () => {
      imageRef.current = img;
      setImageDimensions({ width: img.width, height: img.height });
      
      // Initialize crop area to center of image
      const cropSize = Math.min(img.width, img.height, 400);
      setSettings(prev => ({
        ...prev,
        x: Math.round((img.width - cropSize) / 2),
        y: Math.round((img.height - cropSize) / 2),
        width: cropSize,
        height: cropSize,
      }));
    };
    img.src = url;
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Draw crop overlay on canvas
  useEffect(() => {
    if (!canvasRef.current || !imageRef.current || !imageDimensions) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    // Calculate display scale to fit container
    const maxWidth = container.clientWidth - 40;
    const maxHeight = 500;
    const scale = Math.min(maxWidth / imageDimensions.width, maxHeight / imageDimensions.height, 1);
    setDisplayScale(scale);
    
    canvas.width = imageDimensions.width * scale;
    canvas.height = imageDimensions.height * scale;
    
    // Draw image
    ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    
    // Draw dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Clear crop area (show original image)
    const cropX = settings.x * scale;
    const cropY = settings.y * scale;
    const cropW = settings.width * scale;
    const cropH = settings.height * scale;
    
    ctx.clearRect(cropX, cropY, cropW, cropH);
    ctx.drawImage(
      imageRef.current,
      settings.x, settings.y, settings.width, settings.height,
      cropX, cropY, cropW, cropH
    );
    
    // Draw crop border
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.strokeRect(cropX, cropY, cropW, cropH);
    
    // Draw corner handles
    const handleSize = 10;
    ctx.fillStyle = '#3b82f6';
    
    // Top-left
    ctx.fillRect(cropX - handleSize/2, cropY - handleSize/2, handleSize, handleSize);
    // Top-right
    ctx.fillRect(cropX + cropW - handleSize/2, cropY - handleSize/2, handleSize, handleSize);
    // Bottom-left
    ctx.fillRect(cropX - handleSize/2, cropY + cropH - handleSize/2, handleSize, handleSize);
    // Bottom-right
    ctx.fillRect(cropX + cropW - handleSize/2, cropY + cropH - handleSize/2, handleSize, handleSize);
    
    // Draw grid lines (rule of thirds)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    
    // Vertical lines
    ctx.beginPath();
    ctx.moveTo(cropX + cropW/3, cropY);
    ctx.lineTo(cropX + cropW/3, cropY + cropH);
    ctx.moveTo(cropX + 2*cropW/3, cropY);
    ctx.lineTo(cropX + 2*cropW/3, cropY + cropH);
    // Horizontal lines
    ctx.moveTo(cropX, cropY + cropH/3);
    ctx.lineTo(cropX + cropW, cropY + cropH/3);
    ctx.moveTo(cropX, cropY + 2*cropH/3);
    ctx.lineTo(cropX + cropW, cropY + 2*cropH/3);
    ctx.stroke();
    
  }, [imageDimensions, settings]);


  // Mouse handlers for crop area manipulation
  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / displayScale,
      y: (e.clientY - rect.top) / displayScale,
    };
  };

  const getResizeHandle = (mouseX: number, mouseY: number): string | null => {
    const handleSize = 15 / displayScale;
    const { x, y, width, height } = settings;
    
    // Check corners
    if (Math.abs(mouseX - x) < handleSize && Math.abs(mouseY - y) < handleSize) return 'nw';
    if (Math.abs(mouseX - (x + width)) < handleSize && Math.abs(mouseY - y) < handleSize) return 'ne';
    if (Math.abs(mouseX - x) < handleSize && Math.abs(mouseY - (y + height)) < handleSize) return 'sw';
    if (Math.abs(mouseX - (x + width)) < handleSize && Math.abs(mouseY - (y + height)) < handleSize) return 'se';
    
    return null;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e);
    const handle = getResizeHandle(pos.x, pos.y);
    
    if (handle) {
      setIsResizing(handle);
      setDragStart(pos);
    } else if (
      pos.x >= settings.x && pos.x <= settings.x + settings.width &&
      pos.y >= settings.y && pos.y <= settings.y + settings.height
    ) {
      setIsDragging(true);
      setDragStart(pos);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!imageDimensions) return;
    
    const pos = getMousePos(e);
    
    // Update cursor
    const canvas = canvasRef.current;
    if (canvas) {
      const handle = getResizeHandle(pos.x, pos.y);
      if (handle === 'nw' || handle === 'se') canvas.style.cursor = 'nwse-resize';
      else if (handle === 'ne' || handle === 'sw') canvas.style.cursor = 'nesw-resize';
      else if (
        pos.x >= settings.x && pos.x <= settings.x + settings.width &&
        pos.y >= settings.y && pos.y <= settings.y + settings.height
      ) canvas.style.cursor = 'move';
      else canvas.style.cursor = 'crosshair';
    }
    
    if (!dragStart) return;
    
    const dx = pos.x - dragStart.x;
    const dy = pos.y - dragStart.y;
    
    if (isResizing) {
      setSettings(prev => {
        let newX = prev.x;
        let newY = prev.y;
        let newWidth = prev.width;
        let newHeight = prev.height;
        
        if (isResizing.includes('w')) {
          newX = Math.max(0, Math.min(prev.x + dx, prev.x + prev.width - 20));
          newWidth = prev.width - (newX - prev.x);
        }
        if (isResizing.includes('e')) {
          newWidth = Math.max(20, Math.min(prev.width + dx, imageDimensions.width - prev.x));
        }
        if (isResizing.includes('n')) {
          newY = Math.max(0, Math.min(prev.y + dy, prev.y + prev.height - 20));
          newHeight = prev.height - (newY - prev.y);
        }
        if (isResizing.includes('s')) {
          newHeight = Math.max(20, Math.min(prev.height + dy, imageDimensions.height - prev.y));
        }
        
        // Apply aspect ratio constraint
        if (prev.aspectRatio !== null) {
          if (isResizing.includes('e') || isResizing.includes('w')) {
            newHeight = Math.round(newWidth / prev.aspectRatio);
            if (newY + newHeight > imageDimensions.height) {
              newHeight = imageDimensions.height - newY;
              newWidth = Math.round(newHeight * prev.aspectRatio);
            }
          } else {
            newWidth = Math.round(newHeight * prev.aspectRatio);
            if (newX + newWidth > imageDimensions.width) {
              newWidth = imageDimensions.width - newX;
              newHeight = Math.round(newWidth / prev.aspectRatio);
            }
          }
        }
        
        return { ...prev, x: Math.round(newX), y: Math.round(newY), width: Math.round(newWidth), height: Math.round(newHeight) };
      });
      setDragStart(pos);
    } else if (isDragging) {
      setSettings(prev => {
        const newX = Math.max(0, Math.min(prev.x + dx, imageDimensions.width - prev.width));
        const newY = Math.max(0, Math.min(prev.y + dy, imageDimensions.height - prev.height));
        return { ...prev, x: Math.round(newX), y: Math.round(newY) };
      });
      setDragStart(pos);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(null);
    setDragStart(null);
  };

  const cropImage = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('x', Math.round(settings.x).toString());
      formData.append('y', Math.round(settings.y).toString());
      formData.append('width', Math.round(settings.width).toString());
      formData.append('height', Math.round(settings.height).toString());
      formData.append('output_format', settings.outputFormat);
      formData.append('quality', settings.quality.toString());

      const response = await fetch(getApiUrl('/imagetool/crop'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Crop failed');
      }

      const originalSizeHeader = response.headers.get('X-Original-Size') || response.headers.get('x-original-size');
      const croppedSizeHeader = response.headers.get('X-Cropped-Size') || response.headers.get('x-cropped-size');
      const originalDimensionsHeader = response.headers.get('X-Original-Dimensions') || response.headers.get('x-original-dimensions');
      const croppedDimensionsHeader = response.headers.get('X-Cropped-Dimensions') || response.headers.get('x-cropped-dimensions');

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);

      setResult({
        file: new File([blob], file.name, { type: blob.type }),
        originalSize: parseInt(originalSizeHeader || '0') || file.size,
        croppedSize: parseInt(croppedSizeHeader || '0') || blob.size,
        originalDimensions: originalDimensionsHeader || '',
        croppedDimensions: croppedDimensionsHeader || '',
        downloadUrl,
      });
    } catch (error) {
      console.error('Crop error:', error);
      setError(error instanceof Error ? error.message : 'Crop failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadResult = () => {
    if (!result) return;
    const link = document.createElement('a');
    link.href = result.downloadUrl;
    const originalName = file?.name ? file.name.split('.')[0] : 'cropped_image';
    link.download = `${originalName}_cropped.${settings.outputFormat === 'original' ? 'png' : settings.outputFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFile = () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setFile(null);
    setImageUrl(null);
    setImageDimensions(null);
    setResult(null);
    setError(null);
    imageRef.current = null;
  };

  const resetCrop = () => {
    if (!imageDimensions) return;
    const cropSize = Math.min(imageDimensions.width, imageDimensions.height, 400);
    setSettings(prev => ({
      ...prev,
      x: Math.round((imageDimensions.width - cropSize) / 2),
      y: Math.round((imageDimensions.height - cropSize) / 2),
      width: cropSize,
      height: cropSize,
      aspectRatio: null,
    }));
    setSelectedPreset('free');
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-teal-100 rounded-full">
            <Crop className="w-8 h-8 text-teal-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Image Cropper</h2>
        <p className="text-gray-600">
          Select and crop any region of your image. Perfect for removing unwanted areas.
        </p>
      </div>

      {/* File Upload or Image Preview */}
      {!file ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors"
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600 mb-4">
            Supports PNG, JPG, WebP, BMP, TIFF up to {formatFileSize(API_CONFIG.MAX_FILE_SIZE)}
          </p>
          <input
            type="file"
            accept={API_CONFIG.SUPPORTED_FORMATS.INPUT.join(',')}
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
            id="crop-file-upload"
          />
          <label
            htmlFor="crop-file-upload"
            className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer"
          >
            <FileImage className="w-4 h-4 mr-2" />
            Select Image
          </label>
        </div>
      ) : (
        <>
          {/* Aspect Ratio Presets */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3">Aspect Ratio</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(presets).slice(0, 8).map(([key, preset]) => (
                <button
                  key={key}
                  onClick={() => applyPreset(key)}
                  className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                    selectedPreset === key
                      ? 'border-teal-500 bg-teal-50 text-teal-900'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {/* Canvas for crop selection */}
          <div ref={containerRef} className="bg-gray-100 rounded-lg p-4 flex justify-center">
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              className="rounded shadow-lg"
              style={{ maxWidth: '100%' }}
            />
          </div>

          {/* Crop dimensions display */}
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Crop Area</h3>
              <button
                onClick={resetCrop}
                className="flex items-center text-sm text-gray-600 hover:text-gray-800"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">X Position</label>
                <input
                  type="number"
                  value={Math.round(settings.x)}
                  onChange={(e) => setSettings(prev => ({ ...prev, x: Math.max(0, parseInt(e.target.value) || 0) }))}
                  min="0"
                  max={imageDimensions ? imageDimensions.width - settings.width : 0}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Y Position</label>
                <input
                  type="number"
                  value={Math.round(settings.y)}
                  onChange={(e) => setSettings(prev => ({ ...prev, y: Math.max(0, parseInt(e.target.value) || 0) }))}
                  min="0"
                  max={imageDimensions ? imageDimensions.height - settings.height : 0}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
                <input
                  type="number"
                  value={Math.round(settings.width)}
                  onChange={(e) => {
                    const newWidth = Math.max(20, parseInt(e.target.value) || 20);
                    setSettings(prev => {
                      let newHeight = prev.height;
                      if (prev.aspectRatio !== null) {
                        newHeight = Math.round(newWidth / prev.aspectRatio);
                      }
                      return { ...prev, width: newWidth, height: newHeight };
                    });
                  }}
                  min="20"
                  max={imageDimensions ? imageDimensions.width - settings.x : 1000}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
                <input
                  type="number"
                  value={Math.round(settings.height)}
                  onChange={(e) => {
                    const newHeight = Math.max(20, parseInt(e.target.value) || 20);
                    setSettings(prev => {
                      let newWidth = prev.width;
                      if (prev.aspectRatio !== null) {
                        newWidth = Math.round(newHeight * prev.aspectRatio);
                      }
                      return { ...prev, width: newWidth, height: newHeight };
                    });
                  }}
                  min="20"
                  max={imageDimensions ? imageDimensions.height - settings.y : 1000}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>
            {imageDimensions && (
              <p className="mt-3 text-sm text-gray-500">
                Original: {imageDimensions.width}×{imageDimensions.height}px | 
                Crop: {Math.round(settings.width)}×{Math.round(settings.height)}px
              </p>
            )}
          </div>

          {/* Advanced Settings */}
          <div className="bg-white border rounded-lg">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50"
            >
              <div className="flex items-center">
                <Settings className="w-5 h-5 text-gray-500 mr-2" />
                <span className="font-medium">Output Settings</span>
              </div>
              <span className="text-gray-400">{showAdvanced ? '−' : '+'}</span>
            </button>
            
            {showAdvanced && (
              <div className="border-t p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Output Format
                    </label>
                    <select
                      value={settings.outputFormat}
                      onChange={(e) => setSettings(prev => ({ ...prev, outputFormat: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="original">Keep Original</option>
                      <option value="jpeg">JPEG</option>
                      <option value="png">PNG</option>
                      <option value="webp">WebP</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quality: {settings.quality}%
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={settings.quality}
                      onChange={(e) => setSettings(prev => ({ ...prev, quality: parseInt(e.target.value) }))}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* File info and clear button */}
          <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <FileImage className="w-4 h-4 text-gray-400 mr-2" />
              <span className="text-sm">{file.name}</span>
              <span className="text-xs text-gray-500 ml-2">({formatFileSize(file.size)})</span>
            </div>
            <button
              onClick={clearFile}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Change Image
            </button>
          </div>
        </>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-red-700">{error}</div>
        </div>
      )}

      {/* Crop Button */}
      {file && !result && (
        <button
          onClick={cropImage}
          disabled={isProcessing}
          className="w-full bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Cropping...
            </>
          ) : (
            <>
              <Crop className="w-4 h-4 mr-2" />
              Crop Image
            </>
          )}
        </button>
      )}

      {/* Result */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <h3 className="font-medium text-green-900">Crop Complete!</h3>
          </div>
          
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">{result.file.name}</span>
              <button
                onClick={downloadResult}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Original Size:</span>
                <div className="font-medium">{formatFileSize(result.originalSize)}</div>
              </div>
              <div>
                <span className="text-gray-600">Cropped Size:</span>
                <div className="font-medium">{formatFileSize(result.croppedSize)}</div>
              </div>
              <div>
                <span className="text-gray-600">Original:</span>
                <div className="font-medium">{result.originalDimensions}</div>
              </div>
              <div>
                <span className="text-gray-600">Cropped:</span>
                <div className="font-medium">{result.croppedDimensions}</div>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => { setResult(null); }}
            className="mt-4 w-full py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-100"
          >
            Crop Again
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageCropper;
